import { isEqual, omit } from 'lodash';
import { cloneDeep } from 'lodash/fp';
import React, { useEffect, useState } from 'react';
import {
  DayPickerButton,
  DAY_END,
  DAY_START,
  DEFAULT_START_HOUR,
  DEFAULT_STOP_HOUR,
  PickerProps,
  WEEK_DAYS,
} from '../WeekDayPicker/WeekDayPicker';
import '../WeekDayPicker/WeekDayPicker.pcss';
import './DetailedPicker.pcss';
import { TimePicker } from '../../../../../../../common/components/TimePicker/TimePicker';
import CheckBox from '../../../../../../../common/components/Checkbox';

export const DetailedPicker = ({
  daysData,
  setDaysData,
  useCheckboxStyle,
}: PickerProps) => {
  const [localDaysData, setLocalDaysData] = useState(cloneDeep(daysData));

  useEffect(() => {
    if (!isEqual(daysData, localDaysData)) {
      setDaysData(localDaysData);
    }
  }, [localDaysData]);

  const handleToggleDay = (idx: number) => {
    const isDayOn = Object.keys(localDaysData).includes(idx.toString());
    if (isDayOn) {
      // @ts-ignore
      setLocalDaysData(omit(localDaysData, [idx.toString()]));
    } else {
      setLocalDaysData({
        ...localDaysData,
        [idx.toString()]: {
          startHour: DEFAULT_START_HOUR,
          stopHour: DEFAULT_STOP_HOUR,
        },
      });
    }
  };

  const changeHour = (
    idx: number,
    hourType: 'startHour' | 'stopHour',
    value: string,
  ) => {
    const storedCheckHour =
      localDaysData[idx][hourType === 'startHour' ? 'stopHour' : 'startHour'];

    const storeDataCopy = cloneDeep(localDaysData);

    if (value !== storedCheckHour) {
      storeDataCopy[idx][hourType] = value;
    } else {
      storeDataCopy[idx][hourType] = storedCheckHour;
    }

    if (!isEqual(storeDataCopy, localDaysData)) {
      setLocalDaysData(storeDataCopy);
    }
  };

  const handleAllDay = (idx: number) => {
    const isFullDay =
      localDaysData[idx]?.startHour === DAY_START &&
      localDaysData[idx]?.stopHour === DAY_END;
    const updatedDay = isFullDay
      ? { startHour: DEFAULT_START_HOUR, stopHour: DEFAULT_STOP_HOUR }
      : { startHour: DAY_START, stopHour: DAY_END };
    setLocalDaysData({
      ...localDaysData,
      [idx]: updatedDay,
    });
  };

  return (
    <div className="day-and-time-picker--detailed">
      {WEEK_DAYS.map((day, idx) => {
        const isDayOn = Object.keys(localDaysData).includes(idx.toString());
        return (
          <div className="day-and-time-picker__detailed--day" key={idx}>
            <DayPickerButton
              key={`day-btn-${idx}`}
              idx={idx}
              day={day}
              handleToggle={() => handleToggleDay(idx)}
              isSelected={isDayOn}
              className="week-day-picker__day-detailed"
              useCheckboxStyle={useCheckboxStyle}
            />
            {isDayOn ? (
              <>
                <TimePicker
                  disabled={!isDayOn}
                  onChange={value => changeHour(idx, 'startHour', value)}
                  value={localDaysData[idx]?.startHour ?? DEFAULT_START_HOUR}
                  hideIcon
                />
                <div className="day-and-time-picker__detailed--day-text">-</div>
                <TimePicker
                  disabled={!isDayOn}
                  onChange={value => changeHour(idx, 'stopHour', value)}
                  value={localDaysData[idx]?.stopHour ?? DEFAULT_STOP_HOUR}
                  hideIcon
                />
                <CheckBox
                  key={`day-checkbox-${idx}`}
                  disabled={!isDayOn}
                  className="week-day-picker__checkbox"
                  isChecked={
                    isDayOn &&
                    localDaysData[idx]?.startHour === DAY_START &&
                    localDaysData[idx]?.stopHour === DAY_END
                  }
                  onToggle={() => handleAllDay(idx)}
                />
                <div className="day-and-time-picker__detailed--day-text">
                  All day
                </div>
              </>
            ) : (
              <div className="day-and-time-picker__detailed--day-time-is-not-set">
                time is not set
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
